import { SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import StatStripSkeleton from '@/components/ui/StatStripSkeleton'
import { cn } from '@/lib/utils'

/*
 * Brokers · PV range before its first response: the LOADED page, block for
 * block, in its order and at its heights — the same rule Market's own
 * `MarketBoardStates.tsx` sets out.
 *
 * Every block reuses the loaded component's own box classes — `PVSummary`'s
 * `StatStrip` panel, `PVBucketChart`'s `SectionCard` shell — so it matches at
 * every width, not just a measured one:
 *
 *   controls row     the `StockPicker` and two `BrokerSideFilter`s, each a label
 *                    line over its trigger, and the period control at the right
 *   window note      one line
 *   PV summary       the three-cell panel, each a label, a figure and a line
 *   bucket chart     `SectionCard` head + the 10-column plot (`PVBucketChart`'s
 *                    own loading branch, copied verbatim)
 *
 * Bars only, no words, so nothing here can be mistaken for, or found by a
 * test as, the loaded content.
 */

/** A bar vertically centred in a text line box of `line` height. */
function Line({ line, bar }: { line: string; bar: string }) {
  return (
    <div className={cn('flex items-center', line)}>
      <Skeleton className={cn('rounded-xs', bar)} />
    </div>
  )
}

/** A labelled select's shape: a label line over a `SelectTrigger`. */
function FieldSkeleton({ trigger }: { trigger: string }) {
  return (
    <div className="flex flex-col gap-2">
      <Line line="h-4" bar="h-3 w-10" />
      <Skeleton className={cn('h-11 rounded-sm lg:h-9', trigger)} />
    </div>
  )
}

function ControlsSkeleton() {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <FieldSkeleton trigger="w-60" />
        <FieldSkeleton trigger="w-40" />
        <FieldSkeleton trigger="w-40" />
      </div>
      <Skeleton className="h-9 w-72 rounded-sm" />
    </div>
  )
}

/** `PVSummary`'s three cells: each a label over a 16px figure. */
export function PVSummarySkeleton() {
  return <StatStripSkeleton cells={3} surface="panel" size="m" />
}

/** `PVBucketChart`'s own `SectionCard` shell and its own 8-bar loading branch. */
function BucketChartSkeleton() {
  return (
    <div className={SECTION_CARD}>
      <div className="flex min-h-9 items-center justify-between gap-3">
        <Skeleton className="h-4 w-56 rounded-xs" />
        <Skeleton className="h-3 w-12 rounded-xs" />
      </div>
      <div className="mt-3 flex flex-col gap-2">
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton key={index} className="h-4 w-full" />
        ))}
      </div>
    </div>
  )
}

export function PVRangeSkeleton() {
  return (
    <>
      <ControlsSkeleton />
      <Line line="h-4" bar="h-3 w-32 rounded-xs" />
      <PVSummarySkeleton />
      <BucketChartSkeleton />
      {/* The page's static source footnote wraps to two lines at the 728px
          main column, same as the loaded page — measured, not a rule. */}
      <div className="flex flex-col gap-1">
        <Skeleton className="h-3 w-full max-w-lg rounded-xs" />
        <Skeleton className="h-3 w-2/3 max-w-sm rounded-xs" />
      </div>
    </>
  )
}
